import { useEffect, useState } from "react";
import { Outlet, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, LogOut } from "lucide-react";
import { AppHeader } from "./AppHeader";
import { ConfirmSheet } from "./ConfirmSheet";
import { pendingEventCount } from "../lib/events";
import { startBackgroundSync } from "../offline/backgroundSync";
import { useWriteQueue } from "../offline/writeQueue";
import type { Role } from "../types";

export interface AppOutletContext {
  requestLogout: () => void;
  setHeaderTitle: (title: string | null) => void;
}

export function useAppOutlet(): AppOutletContext {
  return useOutletContext<AppOutletContext>();
}

// The app is a fixed-width frame: everything outside it is plain white so
// the frame reads as the device surface on wide screens. See design.md §14.4.
export function Shell({ role, onLogout }: { role: Role; onLogout: () => void }) {
  const { t } = useTranslation();
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const [headerTitle, setHeaderTitle] = useState<string | null>(null);
  const queue = useWriteQueue();
  // Records not yet sent (answers, progress, activity) at the moment
  // sign-out was asked for: signing out now could lose them, so warn first.
  const [unsent, setUnsent] = useState(0);
  const requestLogout = () => {
    setUnsent(queue.pending + pendingEventCount());
    setConfirmingLogout(true);
  };

  // Teachers' lessons start saving to the phone as soon as they are signed
  // in, and again whenever the connection comes back.
  useEffect(() => {
    if (role !== "teacher") return undefined;
    void startBackgroundSync();
    const retry = () => void startBackgroundSync();
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  }, [role]);

  return (
    <div className="flex min-h-screen w-full justify-center bg-surface">
      <div className="relative w-full max-w-md bg-app-canvas">
        <AppHeader role={role} onLogout={requestLogout} currentTitle={headerTitle} />
        <main className="min-h-screen px-4 pb-10 pt-20">
          <Outlet context={{ requestLogout, setHeaderTitle } satisfies AppOutletContext} />
        </main>
      </div>

      {/*
        Sign-out is confirmed here rather than at each trigger so the header
        menu and the profile screen share one modal. ConfirmSheet pins itself
        to the app column, so the blur stops at the frame edge.
      */}
      <ConfirmSheet
        open={confirmingLogout}
        icon={unsent > 0 ? AlertTriangle : LogOut}
        tone="danger"
        placement="center"
        title={t(unsent > 0 ? "signOutPendingTitle" : "signOutConfirmTitle")}
        message={unsent > 0 ? t("signOutPendingBody", { count: unsent }) : t("signOutConfirmBody")}
        confirmLabel={t(unsent > 0 ? "signOutAnyway" : "signOutConfirm")}
        onConfirm={onLogout}
        onCancel={() => setConfirmingLogout(false)}
      />
    </div>
  );
}
